import { PermissionValues as Permissions } from '@gripp/shared-logic';
import { useParams } from 'react-router-dom';
import { AssetRouteProvider } from '../assets/assetRouteContext';
import { assetSubRoutes } from '../assets/assetSubRoutes';
import { AssetAlertFormPage } from '../assets/components/asset-alert-form-page';
import { AssetRoutineAlerts } from '../assets/components/asset-routine-alerts';
import { AssetRoutineSubmit } from '../assets/components/asset-routine-submit';
import { AssetRoutinesAll } from '../assets/components/asset-routines-all';
import { AssetRoutinesRearrange } from '../assets/components/asset-routines-rearrange';
import { AssetActivity } from '../assets/components/assetActivity';
import { AssetDetail } from '../assets/components/assetDetail';
import { AssetFileEdit } from '../assets/components/assetFileEdit';
import { AssetFilePreview } from '../assets/components/assetFilePreview';
import { AssetFiles } from '../assets/components/assetFiles';
import { AssetNewIssue } from '../assets/components/assetNewIssue';
import { EditAsset } from '../assets/components/edit';
import { Issue } from '../issues/components/issue';
import { Activities } from './components/activities';
import { ActivityNew } from './components/activities-new';
import { ActivityEdit, ActivityEditPanel } from './components/activity-edit';
import { ActivityReassign } from './components/activity-reassign';
import { ActivityRevise } from './components/activity-revise';
import { ActivityConversationPanel } from './components/activityConversationPanel';
import { ActivityFilterPage } from './components/activityFilterPage';
import { IntegrationRunPanel } from './components/integrationRunPanel';

// Thin wrapper so the activities/* wildcard route renders Issue inside a
// provider — giving onIssueDelete a listPath of '/activities' without
// relying on location.pathname string checks.
const ActivityIssue = () => {
  const { issueId = '' } = useParams();
  return (
    <AssetRouteProvider
      value={{
        basePath: `/activities/${issueId}`,
        listPath: '/activities',
        isWorkspaceMode: false,
        issueId,
      }}
    >
      <Issue isComingFromActivities={true} />
    </AssetRouteProvider>
  );
};

export const activityPaths = {
  root: 'activities',
};

export const activityRoutes = [
  {
    path: activityPaths.root,
    element: <Activities />,
    handle: {
      header: {
        titleKey: 'activity.menu.title',
        icon: 'bell-outline',
      },
      requiredPermission: Permissions.activity.read,
    },
    children: [
      {
        path: 'new',
        element: <ActivityNew />,
        handle: {
          panel: { key: 'activity-new', title: 'activity.menu.title' },
          header: {
            titleKey: 'activity.menu.title',
            icon: 'bell-outline',
          },
          requiredPermission: Permissions.issue.create,
        },
      },
      {
        path: 'filters',
        element: <ActivityFilterPage />,
        handle: {
          panel: { key: 'activity-filters', title: 'activity.menu.title' },
          header: {
            titleKey: 'activity.menu.title',
            icon: 'bell-outline',
          },
          requiredPermission: Permissions.activity.read,
        },
      },
      {
        // Per-run integration sync detail (GRIPP #2887, Plan 0019). Additive
        // route: tapping a per-run feed card opens this right-hand panel with the
        // per-asset breakdown. `sync-run` is a static segment, so it ranks above
        // the `:issueId` dynamic child and never collides with it.
        path: 'sync-run/:runId',
        element: <IntegrationRunPanel />,
        handle: {
          panel: {
            key: 'activity-sync-run',
            title: 'integrations.run.detailTitle',
          },
        },
      },
      {
        path: ':issueId',
        element: <ActivityConversationPanel />,
        handle: {
          panel: { key: 'activity-conversation', title: 'activity.menu.title' },
        },
      },
      {
        path: ':issueId/asset/:assetId',
        element: <ActivityConversationPanel />,
        handle: {
          panel: {
            key: 'activity-asset-card',
            title: 'assets.menu.titleSingular',
          },
        },
      },
      {
        // Side Chat (plan 0022 §C). Additive child route mirroring the asset
        // child: `sidechat` is a static segment so it ranks above the `:issueId`
        // dynamic sibling and never collides. Renders the same conversation
        // shell, which reads `messageId` and registers the Side Chat column.
        path: ':issueId/sidechat/:messageId',
        element: <ActivityConversationPanel />,
        handle: {
          panel: { key: 'activity-sidechat', title: 'activity.menu.title' },
        },
      },
      {
        path: ':issueId/edit',
        element: <ActivityEditPanel />,
        handle: {
          panel: { key: 'activity-edit', title: 'issue.menu.edit' },
          header: {
            titleKey: 'activity.menu.title',
            icon: 'bell-outline',
          },
          requiredPermission: Permissions.issue.update,
        },
      },
    ],
  },
  {
    path: `${activityPaths.root}/new`,
    element: <ActivityNew />,
    handle: {
      panel: { key: 'activity-new', title: 'activity.menu.title' },
      header: {
        titleKey: 'activity.menu.title',
        icon: 'bell-outline',
      },
      requiredPermission: Permissions.issue.create,
    },
  },
  {
    path: `${activityPaths.root}/:issueId/edit`,
    element: <ActivityEdit />,
    handle: {
      header: {
        titleKey: 'activity.menu.title',
        icon: 'bell-outline',
      },
      requiredPermission: Permissions.issue.update,
    },
  },
  {
    path: `${activityPaths.root}/:issueId/edit/reassign`,
    element: <ActivityReassign />,
    handle: {
      header: {
        titleKey: 'activity.menu.title',
        icon: 'bell-outline',
      },
      requiredPermission: Permissions.issue.update,
    },
  },
  {
    path: `${activityPaths.root}/:issueId/edit/revise`,
    element: <ActivityRevise />,
    handle: {
      header: {
        titleKey: 'activity.menu.title',
        icon: 'bell-outline',
      },
      requiredPermission: Permissions.issue.update,
    },
  },
  // Shared asset sub-routes with handle.panel metadata for PanelShell transitionKey.
  // These match the same URL patterns as the legacy flat entries below; placing
  // them first ensures React Router picks up handle.panel for the transitionKey.
  // Legacy flat entries are kept per CLAUDE.md's additive-only rule.
  ...assetSubRoutes.map((r) => ({
    ...r,
    path: `${activityPaths.root}/:issueId/asset/:assetId/${r.path}`,
  })),

  {
    path: `${activityPaths.root}/:issueId/asset/:assetId/edit`,
    element: <EditAsset />,
    handle: {
      header: { titleKey: 'topNav.editAsset', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: `${activityPaths.root}/:contextIssueId/asset/:assetId/activity/:issueId`,
    element: <AssetActivity />,
    handle: {
      header: {
        titleKey: 'assets.menu.title',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: `${activityPaths.root}/:contextIssueId/asset/:assetId/activity/:issueId/edit`,
    element: <ActivityEdit />,
    handle: {
      header: { titleKey: 'activity.menu.title', icon: 'bell-outline' },
      requiredPermission: Permissions.issue.update,
    },
  },
  {
    path: `${activityPaths.root}/:contextIssueId/asset/:assetId/activity/:issueId/edit/revise`,
    element: <ActivityRevise />,
    handle: {
      header: {
        titleKey: 'activity.menu.title',
        icon: 'bell-outline',
      },
      requiredPermission: Permissions.issue.update,
    },
  },
  {
    path: `${activityPaths.root}/:contextIssueId/asset/:assetId/activity/:issueId/edit/reassign`,
    element: <ActivityReassign />,
    handle: {
      header: {
        titleKey: 'activity.menu.title',
        icon: 'bell-outline',
      },
      requiredPermission: Permissions.issue.update,
    },
  },
  {
    path: `${activityPaths.root}/:issueId/asset/:assetId/activity/all`,
    element: <AssetDetail />,
    handle: {
      header: {
        titleKey: 'topNav.assetDetail',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: `${activityPaths.root}/:issueId/asset/:assetId/activity/new`,
    element: <AssetNewIssue />,
    handle: {
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.issue.create,
    },
  },
  {
    path: `${activityPaths.root}/:issueId/asset/:assetId/routines/all`,
    element: <AssetRoutinesAll />,
    handle: {
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: `${activityPaths.root}/:issueId/asset/:assetId/routines/:routineId`,
    element: <AssetRoutineSubmit />,
    handle: {
      header: { titleKey: 'assets.menu.title', icon: 'qrcode' },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: `${activityPaths.root}/:issueId/asset/:assetId/routines/edit`,
    element: <AssetRoutinesRearrange />,
    handle: {
      header: {
        titleKey: 'assets.menu.title',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.update,
    },
  },
  {
    path: `${activityPaths.root}/:issueId/asset/:assetId/files/all`,
    element: <AssetFiles />,
    handle: {
      header: {
        titleKey: 'assets.menu.title',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: `${activityPaths.root}/:issueId/asset/:assetId/files/:mediaId`,
    element: <AssetFilePreview />,
    handle: {
      header: {
        titleKey: 'assets.menu.title',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: `${activityPaths.root}/:issueId/asset/:assetId/files/:mediaId/edit`,
    element: <AssetFileEdit />,
    handle: {
      header: {
        titleKey: 'assets.menu.title',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.update,
    },
  },
  {
    path: `${activityPaths.root}/:issueId/asset/:assetId/routines/:routineId/alerts`,
    element: <AssetRoutineAlerts />,
    handle: {
      header: {
        titleKey: 'assets.menu.title',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: `${activityPaths.root}/:issueId/asset/:assetId/routines/:routineId/alerts/new`,
    element: <AssetAlertFormPage />,
    handle: {
      header: {
        titleKey: 'assets.menu.title',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: `${activityPaths.root}/:issueId/asset/:assetId/routines/:routineId/alerts/:alertId/edit`,
    element: <AssetAlertFormPage />,
    handle: {
      header: {
        titleKey: 'assets.menu.title',
        icon: 'qrcode',
      },
      requiredPermission: Permissions.asset.read,
    },
  },
  {
    path: `${activityPaths.root}/*`,
    element: <Activities />,
    handle: {
      header: {
        titleKey: 'activity.menu.title',
        icon: 'bell-outline',
      },
      requiredPermission: Permissions.activity.read,
    },
    children: [
      {
        path: ':issueId',
        element: <ActivityIssue />,
      },
      {
        path: ':issueId/asset/:assetId',
        element: null,
      },
    ],
  },
];
